import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "../App";

const LOGO_RECTANGLE = "https://customer-assets.emergentagent.com/job_hire-race/artifacts/tx9umukp_WhatsApp%20Image%202026-02-22%20at%2015.10.15.jpeg";

export default function SignUpPage() {
  const [formData, setFormData] = useState({ name: "", email: "", password: "", confirmPassword: "", department: "" });
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const navigate = useNavigate();

  useEffect(() => { api.get("/departments").then(r => setDepartments(r.data || [])).catch(() => {}); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      if (!formData.department) { toast.error("Please select your department"); setLoading(false); return; }
      const res = await api.post("/auth/register", {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: "Member",
        department: formData.department,
      });
      const { token, user } = res.data;
      localStorage.setItem("dreamteam_token", token);
      localStorage.setItem("dreamteam_user", JSON.stringify(user));
      toast.success("Account created successfully!");
      navigate("/", { replace: true });
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-800 shadow-2xl p-8">
          <div className="flex flex-col items-center mb-8">
            <img src={LOGO_RECTANGLE} alt="Logo" className="h-12 mb-4 rounded" />
            <h1 className="text-2xl font-bold text-white">Create Account</h1>
            <p className="text-gray-400 text-sm mt-1">Join the Dream Machine</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-gray-300">Full Name</Label>
              <Input type="text" required placeholder="Your name" value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="mt-1 bg-gray-800/50 border-gray-700 text-white" />
            </div>
            <div>
              <Label className="text-gray-300">Email</Label>
              <Input type="email" required placeholder="you@example.com" value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="mt-1 bg-gray-800/50 border-gray-700 text-white" />
            </div>
            <div>
              <Label className="text-gray-300">Password</Label>
              <Input type="password" required placeholder="Min 6 characters" value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="mt-1 bg-gray-800/50 border-gray-700 text-white" />
            </div>
            <div>
              <Label className="text-gray-300">Confirm Password</Label>
              <Input type="password" required placeholder="Confirm password" value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                className="mt-1 bg-gray-800/50 border-gray-700 text-white" />
            </div>
            <div>
              <Label className="text-gray-300">Department *</Label>
              <select value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})}
                required className="mt-1 w-full px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                <option value="" className="bg-gray-900">Select your department...</option>
                {departments.map(d => <option key={d.department_id || d.name} value={d.name} className="bg-gray-900">{d.name}</option>)}
              </select>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2.5">
              {loading ? "Creating account..." : "Sign Up"}
            </Button>
          </form>
          <p className="text-center text-gray-400 text-sm mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-orange-400 hover:text-orange-300 font-medium">Sign in</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
